import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, Info, Plus, Save, Wifi } from 'lucide-react';
import SyncIndicator from 'components/SyncIndicator';
import { useAuth } from 'contexts/AuthContext';
import {
  submitInspeccion, submitCorreccion, anularOriginalCorregida, fetchGuias, addGuia, fetchArbolesInspeccion,
  fetchLineasInspeccion,
} from 'services/inspeccionService';
import { hoyCR, fechaLarga } from 'lib/fechaCR';
import { ESTADO_PILL } from 'lib/estadoUi';
import { leerBorrador, guardarBorrador, borrarBorrador } from 'lib/borrador';
import { leerMedia, guardarMedia, borrarMedia, huboErrorMedia } from 'lib/borradorMedia';
import {
  ITEMS_CON_ARBOL, idsArboles, leerArbolesCache, guardarArbolesCache,
} from 'lib/arbolesCache';
import {
  ITEMS_CON_LINEA, idsLineas, leerLineasCache, guardarLineasCache,
} from 'lib/lineasCache';
import { esHallazgo } from './comun';
import EncabezadoAsistente from './EncabezadoAsistente';
import EncabezadoApp from 'components/EncabezadoApp';
import { ConfirmarSalida, useSalidaPin } from './SalidaPin';
import PasoItem from './PasoItem';
import PasoObservaciones from './PasoObservaciones';
import PasoFirma from './PasoFirma';
import PasoResumen, { MOTIVO_MINIMO } from './PasoResumen';
import PantallaFinal from './PantallaFinal';
import { mensajeError } from 'lib/errores';

// Asistente de la inspección diaria de UNA instalación (canopy, Tarzan Swing):
// una pantalla por paso, pensado para el celular del inspector en una
// plataforma (una mano, sol, a veces sin señal).
//   pasos 0..N-1  un ítem del checklist cada uno
//   paso N        observaciones (fotos y comentario)
//   paso N+1      firma e inspector (con PIN: confirmación en vez de firma)
//   paso N+2      resumen y guardar
// Nada depende de una animación para verse (herramienta de seguridad).
//
// Modo corrección (correccion = { original }): solo admin con SSO. Precarga
// resultados y notas de la original, fija su fecha, pide un motivo, guarda en
// línea con corrige_a y anula la original. Su borrador tiene clave propia.

const listaNatural = (xs) => xs.join(', ');
// "07:12" en hora de Costa Rica (UTC-6 fijo, igual que hoyCR).
const horaCR = (ms) => new Date(ms - 6 * 3600 * 1000).toISOString().slice(11, 16);

function Progreso({ paso, total, pasoObs, esPin }) {
  // Un solo formato: "Paso N de 13" y qué es ese paso.
  let que;
  if (paso < pasoObs) que = `Ítem ${paso + 1} de ${pasoObs}`;
  else if (paso === pasoObs) que = 'Observaciones';
  else if (paso === pasoObs + 1) que = esPin ? 'Confirmación' : 'Firma';
  else que = 'Resumen';
  const texto = `Paso ${paso + 1} de ${total} · ${que}`;
  const pct = Math.round(((paso + 1) / total) * 100);
  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-sm font-bold text-foreground tabular-nums">{texto}</span>
      </div>
      <div
        role="progressbar"
        aria-label="Avance de la inspección"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={paso + 1}
        className="h-2.5 w-full overflow-hidden rounded-full bg-muted"
      >
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// retardoAvance: espera antes de que Aprobado pase al siguiente paso.
// bloqueoToques: tras cambiar de paso se ignoran los botones de estado este
// tiempo, para que un doble toque (con guantes) no apruebe el ítem siguiente
// sin verlo.
// inactividadMs (solo kiosco): sin tocar la tableta ese tiempo, la inspección
// en curso se pausa (queda en el borrador) y se vuelve a la pantalla de
// "continuar o descartar", sin la firma: el siguiente guía no la hereda.
export const INACTIVIDAD_KIOSCO_MS = 10 * 60 * 1000;

// Árboles (o cables) elegidos por ítem en los detalles de una inspección guardada.
const arbolesDe = (detalles) => Object.fromEntries(
  Object.entries(detalles || {}).map(([k, d]) => [k, idsArboles(d?.arboles)]).filter(([, ids]) => ids.length),
);
const lineasDe = (detalles) => Object.fromEntries(
  Object.entries(detalles || {}).map(([k, d]) => [k, idsLineas(d?.lineas)]).filter(([, ids]) => ids.length),
);

// Elementos activos de la instalación (árboles para el ítem fitosanitario,
// cables para "Estado del cable" y "Punto de anclaje"): primero los del caché
// (sirven sin señal en las plataformas) y, con red, se refrescan al abrir y al
// volver la conexión. Si la rpc falla o no existe, queda lo del caché (o nada,
// y entonces no se pide elegir).
function useListaInstalacion(instalacion, { leer, guardar, traer }) {
  const [lista, setLista] = useState(() => leer(instalacion) || []);
  useEffect(() => {
    let vivo = true;
    const refrescar = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      Promise.resolve()
        .then(() => traer(instalacion))
        .then((filas) => {
          if (!vivo || !Array.isArray(filas)) return;
          guardar(instalacion, filas);
          setLista(filas);
        })
        .catch(() => {});
    };
    refrescar();
    window.addEventListener('online', refrescar);
    return () => {
      vivo = false;
      window.removeEventListener('online', refrescar);
    };
    // leer/guardar/traer son funciones de módulo (estables).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instalacion]);
  return lista;
}

const FUENTE_ARBOLES = {
  leer: (i) => leerArbolesCache(i)?.arboles,
  guardar: guardarArbolesCache,
  traer: (i) => fetchArbolesInspeccion(i),
};
const FUENTE_LINEAS = {
  leer: (i) => leerLineasCache(i)?.lineas,
  guardar: guardarLineasCache,
  traer: (i) => fetchLineasInspeccion(i),
};

const notasDe = (detalles) => Object.fromEntries(
  Object.entries(detalles || {}).map(([k, d]) => [k, typeof d?.nota === 'string' ? d.nota : '']),
);

export default function Asistente({
  checklist,
  correccion = null,
  mostrarInstalacion = false,
  onVolverAElegir,
  retardoAvance = 250,
  bloqueoToques = 500,
  inactividadMs = INACTIVIDAD_KIOSCO_MS,
  contarPendientes,
}) {
  const navigate = useNavigate();
  const { esPin, esKiosco, nombrePin, signOut } = useAuth();
  const ITEMS = checklist.items;
  const ITEM_KEYS = checklist.itemKeys;
  const PASO_OBS = ITEMS.length;
  const PASO_FIRMA = PASO_OBS + 1;
  const PASO_RESUMEN = PASO_OBS + 2;
  const TOTAL_PASOS = PASO_RESUMEN + 1;
  const original = correccion?.original || null;
  const espacio = original ? 'correccion' : checklist.id;
  const seccionDe = (item) => checklist.secciones.find((s) => s.id === item.seccion)?.titulo || '';
  const detallesDesdeNotas = (resultados, notas, arb = {}, lin = {}) => Object.fromEntries(
    ITEM_KEYS.filter((k) => esHallazgo(resultados[k]))
      .map((k) => {
        // Las llaves arboles y lineas solo aparecen si hay alguno elegido.
        const d = { nota: notas[k] || '', fotoBlobs: [] };
        const ids = idsArboles(arb?.[k]);
        if (ids.length) d.arboles = ids;
        const idsLin = idsLineas(lin?.[k]);
        if (idsLin.length) d.lineas = idsLin;
        return [k, d];
      }),
  );
  const arbolesInstalacion = useListaInstalacion(checklist.id, FUENTE_ARBOLES);
  const lineasInstalacion = useListaInstalacion(checklist.id, FUENTE_LINEAS);
  const conArbol = (key) => ITEMS_CON_ARBOL.includes(key);
  const conLinea = (key) => ITEMS_CON_LINEA.includes(key);

  // El borrador se lee una sola vez, en el primer render. En una corrección,
  // el "día" del borrador es la fecha de la original; sin borrador se precarga
  // la original (resultados, notas, comentarios e inspector) y se abre el resumen.
  const inicioRef = useRef(null);
  if (inicioRef.current === null) {
    const b = original
      ? leerBorrador(original.fecha, { espacio, keys: ITEM_KEYS, corrigeA: original.id })
      : leerBorrador(hoyCR(), { espacio, keys: ITEM_KEYS });
    let semilla = b;
    if (!b && original) {
      const res = Object.fromEntries(
        ITEM_KEYS.filter((k) => ['aprobado', 'alerta', 'falla'].includes(original.resultados?.[k]))
          .map((k) => [k, original.resultados[k]]),
      );
      const notas = notasDe(original.detalles);
      semilla = {
        fecha: original.fecha,
        paso: PASO_RESUMEN,
        resultados: res,
        notas: Object.fromEntries(Object.entries(notas).filter(([k]) => esHallazgo(res[k]))),
        arboles: arbolesDe(original.detalles),
        lineas: lineasDe(original.detalles),
        comentarios: original.comentarios || '',
        inspector: original.guia_nombre || '',
      };
    }
    inicioRef.current = { borrador: b, semilla };
  }
  const b0 = inicioRef.current.borrador;
  const s0 = inicioRef.current.semilla;
  // Fecha y hora en que empezó la inspección (primer cambio). La fecha se fija
  // al empezar, no al enviar: con PIN la base solo acepta la fecha del día de la
  // sesión, y una inspección empezada 23:50 y enviada 00:05 quedaría rechazada.
  // En una corrección, la fecha es siempre la de la original.
  const fechaRef = useRef(original ? original.fecha : b0?.fecha ?? null);
  const iniciadoRef = useRef(b0?.iniciadoAt ?? null);

  const [paso, setPaso] = useState(s0?.paso ?? 0);
  const [resultados, setResultados] = useState(s0?.resultados ?? {});
  const [detalles, setDetalles] = useState(() => (s0 ? detallesDesdeNotas(s0.resultados, s0.notas, s0.arboles, s0.lineas) : {}));
  const [comentarios, setComentarios] = useState(s0?.comentarios ?? '');
  const [fotoBlobs, setFotoBlobs] = useState([]);
  const [firmaBlob, setFirmaBlob] = useState(null);
  // Con PIN no hay firma dibujada: el inspector marca "Confirmo que realicé
  // esta inspección". Se guarda en el borrador.
  const [confirmado, setConfirmado] = useState(() => !!(esPin && b0?.confirmado));
  const [motivo, setMotivo] = useState(b0?.motivo ?? '');
  const [guia, setGuia] = useState(() => (esPin ? '' : s0?.inspector ?? ''));
  const [guias, setGuias] = useState([]);
  // Kiosco: un borrador del día no se continúa sin aviso. Se muestra quién lo
  // empezó y se elige continuarlo o descartarlo.
  const [retomar, setRetomar] = useState(() => (esKiosco && b0 ? { motivo: 'borrador' } : null));
  // Aviso de borrador recuperado. `fotos` se enciende solo si las fotos y la
  // firma guardadas no se pudieron leer (IndexedDB caído o sin cuota).
  const [aviso, setAviso] = useState(() => (b0 && !esKiosco ? { fotos: false } : null));
  // Fotos y firma del borrador (IndexedDB): hasta leerlas no se escribe nada,
  // para no pisar lo guardado con el estado vacío del primer render.
  const [mediaLista, setMediaLista] = useState(false);
  const [mediaPerdida, setMediaPerdida] = useState(false);
  const [final, setFinal] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [reintentando, setReintentando] = useState(false);
  const [error, setError] = useState('');
  // true cuando se llegó a un ítem con "Ir a …" desde el resumen: la barra
  // ofrece "Volver al resumen" y Aprobado regresa allí.
  const [volverAResumen, setVolverAResumen] = useState(false);
  // Ítem cuyo paso a Aprobado espera confirmación (tiene nota o fotos).
  const [confirmarAprobado, setConfirmarAprobado] = useState(null);
  const bloqueoHastaRef = useRef(0);

  const timerRef = useRef(null);
  const guardandoRef = useRef(false);
  const montadoRef = useRef(true);
  const contenidoRef = useRef(null);
  const pasoPrevioRef = useRef(paso);

  const cancelarAvance = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
      cancelarAvance();
    };
  }, [cancelarAvance]);

  // Lista de guías: solo SSO y kiosko. La sesión PIN no tiene permiso ni la necesita.
  useEffect(() => {
    if (esPin) return undefined;
    let vivo = true;
    Promise.resolve()
      .then(() => fetchGuias())
      .then((g) => { if (vivo) setGuias(Array.isArray(g) ? g : []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [esPin]);

  // Borrador de texto (localStorage): se escribe con cada cambio. Las fotos y la
  // firma van aparte, en IndexedDB (lib/borradorMedia).
  useEffect(() => {
    if (final) return;
    const vacio = !original && paso === 0 && Object.keys(resultados).length === 0 && !comentarios.trim();
    if (vacio) {
      fechaRef.current = null;
      iniciadoRef.current = null;
      borrarBorrador(espacio);
      return;
    }
    if (!fechaRef.current) fechaRef.current = hoyCR();
    if (!iniciadoRef.current) iniciadoRef.current = Date.now();
    const notas = Object.fromEntries(
      Object.entries(detalles)
        .filter(([k]) => esHallazgo(resultados[k]))
        .map(([k, d]) => [k, d?.nota || '']),
    );
    const arboles = Object.fromEntries(
      Object.entries(detalles)
        .filter(([k, d]) => esHallazgo(resultados[k]) && (d?.arboles || []).length)
        .map(([k, d]) => [k, d.arboles]),
    );
    const lineas = Object.fromEntries(
      Object.entries(detalles)
        .filter(([k, d]) => esHallazgo(resultados[k]) && (d?.lineas || []).length)
        .map(([k, d]) => [k, d.lineas]),
    );
    guardarBorrador({
      fecha: fechaRef.current,
      paso,
      resultados,
      notas,
      comentarios,
      inspector: (esPin ? nombrePin : guia) || '',
      iniciadoAt: iniciadoRef.current,
      confirmado: esPin && confirmado,
      motivo: original ? motivo : undefined,
      corrigeA: original ? original.id : undefined,
      arboles,
      lineas,
    }, espacio);
  }, [paso, resultados, detalles, comentarios, final, guia, esPin, nombrePin, confirmado, motivo, original, espacio]);

  // Fotos y firma del borrador: se leen una vez al montar. Sin borrador de este
  // espacio, lo que hubiera (otro día, otra inspección) se borra.
  useEffect(() => {
    let vivo = true;
    if (!b0) {
      borrarMedia(espacio);
      setMediaLista(true);
      return () => { vivo = false; };
    }
    const habiaError = huboErrorMedia(espacio);
    leerMedia(b0.fecha, espacio).then((r) => {
      if (!vivo) return;
      const m = r.ok ? r.media : null;
      if (m) {
        if (m.fotos.length) setFotoBlobs((prev) => (prev.length ? prev : m.fotos));
        // Kiosco: la firma nunca se hereda (quien firma es quien termina).
        // PIN: no hay firma dibujada.
        if (m.firma && !esKiosco && !esPin) setFirmaBlob((prev) => prev || m.firma);
        setDetalles((d) => {
          let cambio = false;
          const n = { ...d };
          Object.entries(m.items).forEach(([k, bs]) => {
            if (n[k] && !(n[k].fotoBlobs || []).length) {
              n[k] = { ...n[k], fotoBlobs: bs };
              cambio = true;
            }
          });
          return cambio ? n : d;
        });
      }
      if (habiaError || !r.ok) {
        setMediaPerdida(true);
        if (!esKiosco) setAviso((a) => ({ ...(a || {}), fotos: true }));
      }
      setMediaLista(true);
    });
    return () => { vivo = false; };
    // Solo al montar: b0, esKiosco y el espacio son los del primer render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fotos y firma: se escriben cuando cambian (no con cada letra de una nota).
  // En el kiosco la firma no se guarda: el siguiente guía no la hereda.
  const ultimoMediaRef = useRef(null);
  useEffect(() => {
    if (!mediaLista || final) return;
    const items = {};
    ITEM_KEYS.forEach((k) => {
      const bs = detalles[k]?.fotoBlobs;
      if (esHallazgo(resultados[k]) && bs?.length) items[k] = bs;
    });
    const firma = esKiosco || esPin ? null : firmaBlob;
    const prev = ultimoMediaRef.current;
    const mismos = prev && prev.fotos === fotoBlobs && prev.firma === firma
      && Object.keys(prev.items).length === Object.keys(items).length
      && Object.keys(items).every((k) => prev.items[k] === items[k]);
    if (mismos) return;
    ultimoMediaRef.current = { items, fotos: fotoBlobs, firma };
    if (!Object.keys(items).length && !fotoBlobs.length && !firma) borrarMedia(espacio);
    else guardarMedia({ fecha: fechaRef.current || hoyCR(), items, fotos: fotoBlobs, firma, espacio });
  }, [mediaLista, final, resultados, detalles, fotoBlobs, firmaBlob, esKiosco, esPin, ITEM_KEYS, espacio]);

  // Al cambiar de paso: arriba y el foco al título (lectores de pantalla).
  useEffect(() => {
    if (pasoPrevioRef.current === paso && !final) return;
    pasoPrevioRef.current = paso;
    bloqueoHastaRef.current = Date.now() + bloqueoToques;
    try {
      window.scrollTo(0, 0);
    } catch (e) { /* entorno sin scroll */ }
    contenidoRef.current?.querySelector('h2')?.focus({ preventScroll: true });
  }, [paso, final]);

  // desdeResumen: el salto viene de un "Ir a …" del resumen.
  const irA = useCallback((n, desdeResumen = false) => {
    cancelarAvance();
    setAviso(null);
    setConfirmarAprobado(null);
    const destino = Math.min(Math.max(n, 0), PASO_RESUMEN);
    setVolverAResumen(desdeResumen && destino !== PASO_RESUMEN);
    setPaso(destino);
  }, [cancelarAvance, PASO_RESUMEN]);

  const itemActual = paso < PASO_OBS ? ITEMS[paso] : null;

  const aplicarMarca = (item, v) => {
    cancelarAvance();
    setConfirmarAprobado(null);
    setResultados((r) => ({ ...r, [item.key]: v }));
    setDetalles((d) => {
      if (esHallazgo(v)) return d[item.key] ? d : { ...d, [item.key]: { nota: '', fotoBlobs: [] } };
      const { [item.key]: _fuera, ...resto } = d;
      return resto;
    });
    if (v === 'aprobado') {
      const desde = paso;
      const destino = volverAResumen ? PASO_RESUMEN : desde + 1;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setAviso(null);
        if (destino === PASO_RESUMEN) setVolverAResumen(false);
        setPaso((p) => (p === desde ? destino : p));
      }, retardoAvance);
    }
  };

  const marcar = (v) => {
    const item = itemActual;
    if (!item) return;
    // Doble toque: el segundo toque cae en el paso nuevo; se ignora.
    if (Date.now() < bloqueoHastaRef.current) return;
    const d = detalles[item.key];
    const tieneAlgo = !!((d?.nota || '').trim() || (d?.fotoBlobs || []).length);
    if (v === 'aprobado' && esHallazgo(resultados[item.key]) && tieneAlgo) {
      // No se borra en silencio lo que el inspector escribió o fotografió.
      cancelarAvance();
      setConfirmarAprobado(item.key);
      return;
    }
    aplicarMarca(item, v);
  };

  // Solo se actualiza un ítem que sigue en Alerta o Falla: una foto que termina
  // de comprimirse después de marcar Aprobado no vuelve a crear su detalle.
  const setNota = (key, nota) =>
    setDetalles((d) => (d[key] ? { ...d, [key]: { ...d[key], nota } } : d));
  const setFotosItem = (key, blobs) =>
    setDetalles((d) => (d[key] ? { ...d, [key]: { ...d[key], fotoBlobs: blobs } } : d));
  const setArbolesItem = (key, ids) =>
    setDetalles((d) => (d[key] ? { ...d, [key]: { ...d[key], arboles: idsArboles(ids) } } : d));
  const setLineasItem = (key, ids) =>
    setDetalles((d) => (d[key] ? { ...d, [key]: { ...d[key], lineas: idsLineas(ids) } } : d));

  const conteo = useMemo(() => {
    const vals = ITEM_KEYS.map((k) => resultados[k]);
    return {
      aprobado: vals.filter((v) => v === 'aprobado').length,
      alerta: vals.filter((v) => v === 'alerta').length,
      falla: vals.filter((v) => v === 'falla').length,
    };
  }, [resultados, ITEM_KEYS]);
  const faltan = ITEMS.filter((it) => !resultados[it.key]);
  const sinNota = ITEMS.filter((it) => esHallazgo(resultados[it.key]) && !(detalles[it.key]?.nota || '').trim());
  const inspector = esPin ? (nombrePin || '').trim() : guia.trim();

  const problemas = [];
  if (faltan.length) {
    // El "Ir" de cada ítem que falta está en la lista del resumen.
    const nombres = listaNatural(faltan.map((it) => it.label));
    problemas.push({
      id: 'faltan',
      texto: faltan.length === 1
        ? `Falta 1 ítem por marcar: ${nombres}.`
        : `Faltan ${faltan.length} ítems por marcar: ${nombres}.`,
    });
  }
  sinNota.forEach((it) => problemas.push({
    id: `nota-${it.key}`,
    texto: `${it.label}: falta describir el hallazgo.`,
    boton: `Ir a ${it.label}`,
    paso: ITEMS.indexOf(it),
  }));
  // En una Falla la foto es obligatoria (en Alerta sigue opcional).
  ITEMS.filter((it) => resultados[it.key] === 'falla' && !(detalles[it.key]?.fotoBlobs || []).length)
    .forEach((it) => problemas.push({
      id: `foto-${it.key}`,
      texto: `${it.label}: falta la foto de la falla.`,
      boton: `Ir a ${it.label}`,
      paso: ITEMS.indexOf(it),
    }));
  // Ítem fitosanitario en Alerta o Falla: con árboles cargados, hay que elegir
  // al menos uno. Sin lista (rpc caída y sin caché) no se pide.
  if (arbolesInstalacion.length) {
    ITEMS.filter((it) => conArbol(it.key) && esHallazgo(resultados[it.key])
      && !(detalles[it.key]?.arboles || []).length)
      .forEach((it) => problemas.push({
        id: `arbol-${it.key}`,
        texto: `${it.label}: falta elegir el árbol.`,
        boton: `Ir a ${it.label}`,
        paso: ITEMS.indexOf(it),
      }));
  }
  // Estado del cable y punto de anclaje en Alerta o Falla: igual, con cables.
  if (lineasInstalacion.length) {
    ITEMS.filter((it) => conLinea(it.key) && esHallazgo(resultados[it.key])
      && !(detalles[it.key]?.lineas || []).length)
      .forEach((it) => problemas.push({
        id: `linea-${it.key}`,
        texto: `${it.label}: falta elegir el cable.`,
        boton: `Ir a ${it.label}`,
        paso: ITEMS.indexOf(it),
      }));
  }
  if (!inspector) {
    problemas.push(esPin
      ? { id: 'inspector', texto: 'Falta el nombre del inspector. Salga y vuelva a entrar con su PIN.' }
      : { id: 'inspector', texto: 'Falta el nombre del inspector.', boton: 'Escribir el nombre', paso: PASO_FIRMA });
  }
  if (esPin) {
    if (!confirmado) {
      problemas.push({
        id: 'confirmacion',
        texto: 'Falta confirmar que usted realizó esta inspección.',
        boton: 'Ir a confirmar',
        paso: PASO_FIRMA,
      });
    }
  } else if (!firmaBlob) {
    problemas.push({ id: 'firma', texto: 'Falta la firma.', boton: 'Ir a firmar', paso: PASO_FIRMA });
  }
  if (original && motivo.trim().length < MOTIVO_MINIMO) {
    problemas.push({
      id: 'motivo',
      texto: `Falta el motivo de la corrección (al menos ${MOTIVO_MINIMO} caracteres), abajo.`,
    });
  }
  const valido = problemas.length === 0;

  const guardar = async () => {
    if (guardandoRef.current || !valido) return;
    guardandoRef.current = true;
    setGuardando(true);
    setError('');
    const form = {
      fecha: fechaRef.current || hoyCR(),
      fechaHora: new Date().toISOString(),
      guiaNombre: inspector,
      resultados,
      // Solo los ítems que siguen en Alerta o Falla llevan detalle.
      detalles: Object.fromEntries(Object.entries(detalles).filter(([k]) => esHallazgo(resultados[k]))),
      comentarios,
      // Con PIN no hay firma dibujada: firma_url queda null.
      firmaBlob: esPin ? null : firmaBlob,
      fotoBlobs,
      instalacion: checklist.id,
      iniciadaAt: iniciadoRef.current,
    };
    try {
      let fin;
      if (original) {
        const r = await submitCorreccion({ ...form, corrigeA: original.id, motivo: motivo.trim() });
        fin = { correccion: true, id: r.id, anulada: !!r.anulada, errorAnulacion: r.errorAnulacion || null };
      } else {
        const res = await submitInspeccion(form);
        fin = { offline: !!res?.offline };
      }
      // Igual que antes: la lista de guías aprende el nombre (no en modo PIN).
      if (!esPin) Promise.resolve().then(() => addGuia(inspector)).catch(() => {});
      borrarBorrador(espacio);
      borrarMedia(espacio);
      if (montadoRef.current) setFinal({ ...fin, fallas: conteo.falla });
    } catch (e) {
      if (montadoRef.current) setError(mensajeError(e, 'No se pudo guardar la inspección.'));
    } finally {
      guardandoRef.current = false;
      if (montadoRef.current) setGuardando(false);
    }
  };

  const reintentarAnular = async () => {
    if (!final?.correccion || reintentando) return;
    setReintentando(true);
    const r = await anularOriginalCorregida(original.id, final.id, motivo.trim());
    if (!montadoRef.current) return;
    setReintentando(false);
    setFinal((f) => ({ ...f, anulada: r.ok, errorAnulacion: r.ok ? null : r.error }));
  };

  const nuevaInspeccion = () => {
    cancelarAvance();
    borrarBorrador(espacio);
    borrarMedia(espacio);
    setMediaPerdida(false);
    fechaRef.current = null;
    iniciadoRef.current = null;
    setRetomar(null);
    salida.cancelar();
    setResultados({});
    setDetalles({});
    setComentarios('');
    setFotoBlobs([]);
    setFirmaBlob(null);
    setConfirmado(false);
    setGuia('');
    setError('');
    setAviso(null);
    setVolverAResumen(false);
    setConfirmarAprobado(null);
    setFinal(null);
    setPaso(0);
  };
  const nuevaInspeccionRef = useRef(null);
  nuevaInspeccionRef.current = nuevaInspeccion;

  const fechaVista = original ? original.fecha : fechaRef.current || hoyCR();
  const subtitulo = [
    fechaLarga(fechaVista),
    mostrarInstalacion || original ? checklist.nombre : null,
    esPin ? nombrePin : null,
  ].filter(Boolean).join(' · ');

  // Salir (solo PIN): el teléfono o la tableta se comparte, así que el borrador
  // no se queda para el siguiente. Si hay algo marcado o inspecciones sin
  // enviar en la cola, se confirma antes.
  const hayAvance = !final && (paso > 0 || Object.keys(resultados).length > 0 || !!comentarios.trim());
  const salir = () => {
    cancelarAvance();
    salida.cancelar();
    borrarBorrador(espacio);
    borrarMedia(espacio);
    signOut?.();
  };
  const salida = useSalidaPin({ salir, contarPendientes });

  // Kiosco: inactividad. Cualquier toque o tecla reinicia la cuenta. Al vencer,
  // con una inspección a medias se pausa (borrador intacto, firma fuera) y se
  // muestra "continuar o descartar"; en la pantalla final se vuelve a empezar.
  const estadoRef = useRef({});
  estadoRef.current = { hayAvance, final, retomar };
  useEffect(() => {
    if (!esKiosco || !(inactividadMs > 0)) return undefined;
    let t = null;
    const vencer = () => {
      const s = estadoRef.current;
      if (s.final) {
        nuevaInspeccionRef.current?.();
      } else if (s.hayAvance && !s.retomar) {
        cancelarAvance();
        setConfirmarAprobado(null);
        setFirmaBlob(null);
        setRetomar({ motivo: 'inactividad' });
      }
    };
    const reiniciar = () => {
      if (t) clearTimeout(t);
      t = setTimeout(vencer, inactividadMs);
    };
    const eventos = ['pointerdown', 'keydown', 'input', 'touchstart', 'click'];
    eventos.forEach((ev) => window.addEventListener(ev, reiniciar, true));
    reiniciar();
    return () => {
      if (t) clearTimeout(t);
      eventos.forEach((ev) => window.removeEventListener(ev, reiniciar, true));
    };
  }, [esKiosco, inactividadMs, cancelarAvance]);

  const retomarRef = useRef(null);
  useEffect(() => {
    if (retomar) retomarRef.current?.focus();
  }, [retomar]);

  let contenido;
  if (final) {
    contenido = (
      <PantallaFinal
        offline={final.offline}
        fallas={final.fallas}
        nombreInstalacion={checklist.nombre}
        correccion={final.correccion
          ? {
            anulada: final.anulada,
            errorAnulacion: final.errorAnulacion,
            reintentando,
            onReintentar: reintentarAnular,
          }
          : null}
      />
    );
  } else if (itemActual) {
    contenido = (
      <PasoItem
        key={itemActual.key}
        item={itemActual}
        seccion={seccionDe(itemActual)}
        valor={resultados[itemActual.key]}
        detalle={detalles[itemActual.key]}
        onMarcar={marcar}
        confirmando={confirmarAprobado === itemActual.key}
        onConfirmarAprobado={() => aplicarMarca(itemActual, 'aprobado')}
        onCancelarAprobado={() => setConfirmarAprobado(null)}
        onNota={(t) => setNota(itemActual.key, t)}
        onFotos={(b) => setFotosItem(itemActual.key, b)}
        arboles={conArbol(itemActual.key) ? arbolesInstalacion : null}
        onArboles={(ids) => setArbolesItem(itemActual.key, ids)}
        lineas={conLinea(itemActual.key) ? lineasInstalacion : null}
        onLineas={(ids) => setLineasItem(itemActual.key, ids)}
      />
    );
  } else if (paso === PASO_OBS) {
    contenido = (
      <PasoObservaciones
        fotoBlobs={fotoBlobs}
        onFotos={setFotoBlobs}
        comentarios={comentarios}
        onComentarios={setComentarios}
      />
    );
  } else if (paso === PASO_FIRMA) {
    contenido = (
      <PasoFirma
        esPin={esPin}
        nombrePin={nombrePin}
        guia={guia}
        onGuia={setGuia}
        guias={guias}
        firmaBlob={firmaBlob}
        onFirma={setFirmaBlob}
        confirmado={confirmado}
        onConfirmar={setConfirmado}
      />
    );
  } else {
    contenido = (
      <PasoResumen
        items={ITEMS}
        resultados={resultados}
        detalles={detalles}
        comentarios={comentarios}
        fotoBlobs={fotoBlobs}
        inspector={inspector}
        problemas={problemas}
        conteo={conteo}
        faltan={faltan.length}
        onIr={(n) => irA(n, true)}
        error={error}
        nombreInstalacion={checklist.nombre}
        correccion={original ? { motivo, onMotivo: setMotivo } : null}
        arboles={arbolesInstalacion}
        lineas={lineasInstalacion}
      />
    );
  }

  const valorItem = itemActual ? resultados[itemActual.key] : undefined;
  // En un ítem, Siguiente exige una marca (y la nota si es hallazgo).
  const puedeSeguir = !itemActual
    || (!!valorItem && !(esHallazgo(valorItem) && !(detalles[itemActual.key]?.nota || '').trim()));

  if (retomar && !final) {
    const marcados = Object.keys(resultados).length;
    const quien = (esPin ? nombrePin : guia) || '';
    const hora = iniciadoRef.current ? horaCR(iniciadoRef.current) : '';
    contenido = (
      <div role="alertdialog" aria-labelledby="retomar-titulo" className={`rounded-2xl border-2 p-4 ${ESTADO_PILL.alerta}`}>
        <h2 id="retomar-titulo" tabIndex={-1} className="text-xl font-bold leading-tight text-foreground focus:outline-none">
          {retomar.motivo === 'inactividad'
            ? 'Inspección en pausa por inactividad'
            : 'Hay una inspección de hoy sin terminar'}
        </h2>
        <p className="mt-2 text-base text-foreground">
          {quien ? `La empezó ${quien}` : 'La empezó alguien que todavía no escribió su nombre'}
          {hora ? ` a las ${hora}` : ''}. Lleva {marcados} de {ITEMS.length} ítems marcados.
        </p>
        <p className="mt-1 text-base text-foreground">
          Si usted no es esa persona, descártela y empiece la suya. La firma se debe hacer de nuevo.
        </p>
        {mediaPerdida ? (
          <p className="mt-1 text-base text-foreground">
            Las fotos no se pudieron recuperar en esta tableta. Si había tomado fotos, tómelas de nuevo.
          </p>
        ) : null}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <button
            ref={retomarRef}
            type="button"
            onClick={() => { setRetomar(null); setAviso(null); }}
            className="min-h-[56px] flex-1 rounded-2xl bg-primary px-4 text-lg font-bold text-white shadow-green"
          >
            Continuar la inspección
          </button>
          <button
            type="button"
            onClick={nuevaInspeccion}
            className="min-h-[56px] flex-1 rounded-2xl border-2 border-foreground/30 bg-white px-4 text-lg font-bold text-foreground"
          >
            Descartar y empezar de nuevo
          </button>
        </div>
      </div>
    );
  }
  const pausado = !!retomar && !final;
  const puedeVolverAElegir = paso === 0 && !!onVolverAElegir && !original;

  return (
    <>
      <EncabezadoApp onSalir={() => salida.pedir({ hayAvance })} hayAvance={hayAvance} />
      <div className="mx-auto w-full max-w-xl px-4">
      <EncabezadoAsistente
        titulo={original ? 'Corrección de inspección' : 'Inspección diaria'}
        subtitulo={subtitulo}
      />

      <main className="pb-40 pt-4">
        {original && !final ? (
          <div data-aviso="correccion" className={`mb-4 rounded-2xl border-2 p-4 text-base text-foreground ${ESTADO_PILL.alerta}`}>
            <p className="font-bold">
              Corrección de la inspección del {fechaLarga(original.fecha)}
              {original.guia_nombre ? ` (${original.guia_nombre})` : ''}.
            </p>
            <p className="mt-1 flex items-start gap-2">
              <Wifi size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Requiere conexión: se guarda en línea, no queda en el teléfono. Al guardar, la original se anula con su motivo.
              </span>
            </p>
          </div>
        ) : null}
        {!final && !pausado && <Progreso paso={paso} total={TOTAL_PASOS} pasoObs={PASO_OBS} esPin={esPin} />}
        {!final && !original && (
          <div className="mb-3 empty:hidden">
            <SyncIndicator />
          </div>
        )}
        {salida.confirmar && (
          <ConfirmarSalida
            hayAvance={salida.confirmar.hayAvance}
            pendientes={salida.confirmar.pendientes}
            onCancelar={salida.cancelar}
            onSalir={salir}
          />
        )}
        {aviso && !final && (
          <div role="status" className="mb-4 rounded-2xl border border-primary/25 bg-primary/5 p-4 text-base text-foreground">
            <p className="flex items-start gap-2 font-semibold">
              <Info size={20} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
              <span>
                {original
                  ? 'Se recuperó la corrección que estaba en curso.'
                  : 'Se recuperó la inspección de hoy que estaba en curso.'}
              </span>
            </p>
            {aviso.fotos ? (
              <p className="mt-1">
                {esPin
                  ? 'Las fotos guardadas no se pudieron recuperar en este teléfono. Si había tomado fotos, tómelas de nuevo.'
                  : 'Las fotos y la firma guardadas no se pudieron recuperar en este teléfono. Si había tomado fotos, tómelas de nuevo y vuelva a firmar.'}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => setAviso(null)}
              className="mt-2 min-h-[48px] rounded-xl px-3 text-base font-semibold text-primary underline"
            >
              Entendido
            </button>
          </div>
        )}
        <div ref={contenidoRef}>{contenido}</div>
      </main>

      {/* Barra de acciones fija abajo, con el margen seguro del iPhone. En pausa
          (kiosco) no se muestra: primero se elige continuar o descartar. */}
      {!pausado && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white px-4 pb-safe pt-3 shadow-[0_-4px_16px_-6px_rgba(28,25,23,0.12)]">
          <div className="mx-auto flex max-w-xl gap-3">
            {final && final.correccion ? (
              <button
                type="button"
                onClick={() => navigate(`/inspecciones/${final.id}`)}
                className="flex min-h-[56px] flex-1 items-center justify-center gap-2 rounded-2xl bg-primary px-4 text-lg font-bold text-white shadow-green"
              >
                <Eye size={22} strokeWidth={2.5} aria-hidden="true" />
                Ver la corrección
              </button>
            ) : null}
            {final && !final.correccion ? (
              <button
                type="button"
                onClick={nuevaInspeccion}
                className="flex min-h-[56px] flex-1 items-center justify-center gap-2 rounded-2xl bg-primary px-4 text-lg font-bold text-white shadow-green"
              >
                <Plus size={22} strokeWidth={2.5} aria-hidden="true" />
                Nueva inspección
              </button>
            ) : null}
            {!final ? (
              <>
                <button
                  type="button"
                  onClick={() => (puedeVolverAElegir ? onVolverAElegir() : irA(paso - 1))}
                  disabled={(paso === 0 && !puedeVolverAElegir) || guardando}
                  className="flex min-h-[56px] flex-none items-center justify-center gap-1.5 rounded-2xl border-2 border-border bg-white px-4 text-base font-semibold text-foreground disabled:opacity-40"
                >
                  <ArrowLeft size={20} aria-hidden="true" />
                  Atrás
                </button>
                {paso === PASO_RESUMEN ? (
                  <button
                    type="button"
                    onClick={guardar}
                    disabled={!valido || guardando}
                    className="flex min-h-[56px] flex-1 items-center justify-center gap-2 rounded-2xl bg-primary px-3 text-base font-bold leading-tight sm:text-lg text-white shadow-green disabled:cursor-not-allowed disabled:bg-muted disabled:text-foreground disabled:shadow-none"
                  >
                    <Save size={22} strokeWidth={2.25} aria-hidden="true" />
                    {guardando ? 'Guardando…' : original ? 'Guardar corrección' : 'Guardar inspección'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => irA(volverAResumen ? PASO_RESUMEN : paso + 1)}
                    disabled={!puedeSeguir}
                    className={`flex min-h-[56px] flex-1 items-center justify-center gap-2 rounded-2xl bg-primary px-3 font-bold leading-tight text-white shadow-green disabled:cursor-not-allowed disabled:bg-muted disabled:text-foreground disabled:shadow-none ${
                      volverAResumen ? 'text-base sm:text-lg' : 'text-lg'
                    }`}
                  >
                    {volverAResumen ? 'Volver al resumen' : 'Siguiente'}
                    <ArrowRight size={22} strokeWidth={2.5} aria-hidden="true" />
                  </button>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
      </div>
    </>
  );
}
